import API_ENDPOINTS from '../config/apiConfig';
import { api } from './api'; // Reuse the shared api instance

// Cache for labor budget data (same structure as financial budget cache)
export const laborBudgetCache = {
  cacheExpiry: 2 * 60 * 1000, // 2 minutes
  invalidateCache: () => {
    Object.keys(laborBudgetCache).forEach((key) => {
      if (key !== 'invalidateCache' && key !== 'cacheExpiry') {
        delete laborBudgetCache[key];
      }
    });

  },
};

// Helper to build query string for standard filters
const buildQuery = ({ orgId, orgCode, locationId, year }) => {
  const params = [];
  if (orgId) {
    params.push(`orgId=${orgId}`);
  } else if (orgCode) {
    params.push(`orgCode=${orgCode}`);
  }

  if (locationId) {
    params.push(`locationId=${locationId}`);
  }

  if (year) {
    params.push(`startDate=${year}-01-01`);
    params.push(`endDate=${year}-12-31`);
  }

  return params.length > 0 ? `?${params.join('&')}` : '';
};

// Fetch labor budgets with optional filters
export const getLaborBudgets = async (filters = {}) => {
  try {
    const url = `${API_ENDPOINTS.LABOR_BUDGET}${buildQuery(filters)}`;

    // Generate cache key
    const cacheKey = JSON.stringify(filters);
    const now = Date.now();
    if (
      laborBudgetCache[cacheKey] &&
      laborBudgetCache[cacheKey].timestamp &&
      now - laborBudgetCache[cacheKey].timestamp < laborBudgetCache.cacheExpiry
    ) {

      return laborBudgetCache[cacheKey].data;
    }

    const response = await api.get(url);

    laborBudgetCache[cacheKey] = { data: response.data || [], timestamp: now };
    return response.data || [];
  } catch (error) {
    console.error('Error in getLaborBudgets:', error);
    throw error;
  }
};

export const createLaborBudget = async (data) => {
  try {
    // Basic validation – require orgId (or orgCode), locationId & positionId when using driver mode or budgetDate in monthly mode
    if ((!data.orgId && !data.orgCode) || !data.locationId || (!data.positionId && !data.budgetDate)) {
      throw new Error('Missing required fields for labor budget entry');
    }
    const response = await api.post(API_ENDPOINTS.LABOR_BUDGET, data);
    laborBudgetCache.invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error in createLaborBudget:', error);
    throw error;
  }
};

export const createBulkLaborBudgets = async (entries = []) => {
  try {
    if (!Array.isArray(entries)) {
      throw new Error('Input must be an array');
    }
    const response = await api.post(API_ENDPOINTS.LABOR_BUDGET, entries);
    laborBudgetCache.invalidateCache();
    return response.data || [];
  } catch (error) {
    console.error('Error in createBulkLaborBudgets:', error);
    throw error;
  }
};

export const updateLaborBudget = async (data) => {
  try {
    if (!data.id) {
      throw new Error('Labor budget entry ID is required for update');
    }
    const response = await api.put(API_ENDPOINTS.LABOR_BUDGET, data);
    laborBudgetCache.invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error in updateLaborBudget:', error);
    throw error;
  }
};

export const deleteLaborBudget = async (id) => {
  try {
    if (!id) throw new Error('ID required');
    await api.delete(`${API_ENDPOINTS.LABOR_BUDGET}?id=${id}`);
    laborBudgetCache.invalidateCache();
    return true;
  } catch (error) {
    console.error('Error in deleteLaborBudget:', error);
    throw error;
  }
};
export const getMonthlyLaborBudgets = async ({ orgId, locationId, year }) => {
  const response = await api.get(API_ENDPOINTS.LABOR_BUDGET_MONTHS, { params: { orgId, locationId, year } });
  return response.data;
};
export const saveMonthlyLaborBudgets = async (data) => {
  const response = await api.put(API_ENDPOINTS.LABOR_BUDGET_MONTHS, data);
  laborBudgetCache.invalidateCache();
  return response.data;
};
export const resetMonthlyLaborBudget = async (params) => {
  await api.delete(API_ENDPOINTS.LABOR_BUDGET_MONTHS, { params });
  laborBudgetCache.invalidateCache();
};

export const getPublishedLaborBudgetVersions = async ({ orgId, locationId, year }) => {
  const response = await api.get(API_ENDPOINTS.FORECAST_V3_VERSIONS, { params: { orgId, locationId } });
  return (response.data || []).filter(v => v.versionType === 'budget' && v.status === 'published' && Number(v.year) === Number(year));
};
export const importPublishedLaborBudget = async (data) => {
  const response = await api.post(`${API_ENDPOINTS.LABOR_BUDGET_MONTHS}/import`, data, { timeout: 180000 });
  laborBudgetCache.invalidateCache();
  return response.data;
};
